import { Box, FormControlLabel, Modal, Radio, RadioGroup, Typography } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { Canceler } from 'axios';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RouteComponentProps, useLocation, useRouteMatch } from 'react-router-dom';
import { RoundedButton } from '../../../components/RoundedButton';
import SearchTextField from '../../../components/SearchTextField';
import useBackdrop from '../../../hooks/useBackdrop';
import useConfirmDialog from '../../../hooks/useConfirmDialog';
import useSnackbar from '../../../hooks/useSnackbar';
import api from '../../../services/api/base';
import { deleteFeaturedCard, getFeaturedCards } from '../../../services/api/FeaturedCard';
import { FeaturedCardType, IFeaturedCard } from '../../../services/types';
import { RouteNames } from '../../config';
import AddModal from './Create/AddModal';
import { FeaturedCardList } from './FeaturedCardList';

interface IProps {}

interface IParamProps {
  trackingId: string;
}

const FeaturedCard = (props: IProps & RouteComponentProps): JSX.Element => {
  const path = RouteNames.FeaturedCard;
  const match = useRouteMatch<IParamProps>(`${path}/v/:trackingId`);

  const location = useLocation();
  const trackingId = match?.params?.trackingId;

  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingFeaturedCard, setEditingFeaturedCard] = useState<IFeaturedCard>();

  const [featuredCards, setFeaturedCards] = useState<IFeaturedCard[]>();
  const [featuredCardsTotalCount, setFeaturedCardsTotalCount] = useState<number | undefined>(0);
  const [filterType, setFilterType] = useState<FeaturedCardType | 'all'>('all');
  const [searchKey, setSearchKey] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(20);

  const initialTrackingIds = trackingId ? [trackingId] : [];

  const confirm = useConfirmDialog();
  const backdrop = useBackdrop();
  const snackbar = useSnackbar();

  const canceler = useRef<Canceler>(() => undefined);

  const fetchFeaturedCards = async (pageNum: number) => {
    canceler.current();

    try {
      const [fetchPromise, cancel] = getFeaturedCards(filterType === 'all' ? undefined : filterType, searchKey, pageNum + 1, rowsPerPage);
      canceler.current = cancel;

      const result = await fetchPromise;
      if (result.data?.items) {
        setFeaturedCards(result.data.items);
        setFeaturedCardsTotalCount(result.data.totalCount);
      }
    } catch (e) {
      if (api.isCancel(e)) return;
    }
  };

  useEffect(() => {
    fetchFeaturedCards(page);
  }, [page, rowsPerPage]);
  useEffect(() => {
    fetchFeaturedCards(0);
  }, [searchKey, filterType]);

  useEffect(() => {
    const fetchData = async () => {
      if (location.pathname === RouteNames.FeaturedCard) {
        !featuredCards && fetchFeaturedCards(0);
      }
    };

    fetchData();

    return () => canceler.current();
  }, [location.pathname, featuredCards]);

  const handleRequestFetch = useCallback((page, rowsPerPage) => {
    setPage(page);
    setRowsPerPage(rowsPerPage);
  }, []);

  const handleDeleteButtonClick = useCallback(
    (fc: IFeaturedCard) => {
      const onProceed = async () => {
        if (!fc.id) {
          return;
        }

        if (featuredCards === undefined) {
          return;
        }

        const idx = featuredCards.findIndex(f => f.id === fc.id);

        try {
          backdrop.show();
          const result = await deleteFeaturedCard(fc.id);

          if (result.status === 200) {
            if (result.data.success === true) {
              const cloned = [...featuredCards];
              cloned.splice(idx, 1);

              setFeaturedCards(cloned);

              snackbar.success('Deleted successfully.');
            } else {
              snackbar.error(result.data.message);
            }
          } else {
            snackbar.error(`An error has occurred. ${result.statusText}`);
          }
        } catch (e) {
          snackbar.error(`An error has occurred. ${e.message}`);
        } finally {
          backdrop.hide();
        }
      };

      confirm.open({
        title: 'Delete',
        text: 'Are you sure you want to delete this card?',
        onProceed: () => {
          onProceed();
        },
      });
    },
    [featuredCards, backdrop, confirm, snackbar],
  );

  const handleError = useCallback(
    e => {
      snackbar.error(`An error has occurred. ${e.message}`);
    },
    [snackbar],
  );

  const handleClickCreate = useCallback(() => {
    setEditingFeaturedCard(undefined);
    setModalMode('create');
    setShowModal(true);
  }, []);

  const handleClickEdit = useCallback((fc: IFeaturedCard) => {
    setEditingFeaturedCard(fc);
    setModalMode('edit');
    setShowModal(true);
  }, []);

  const getLinkUri = useCallback((trackingId?: string) => (trackingId ? `${RouteNames.FeaturedCard}/v/${trackingId}` : ''), []);

  const onSaveFeaturedCard = useCallback(() => {
    setShowModal(false);
    fetchFeaturedCards(0);
  }, []);

  return (
    <div style={{ maxWidth: '1164px' }}>
      <Box marginBottom={2}>
        <RoundedButton Icon={<AddIcon color="primary" />} label={'Create a featured card'} onClick={handleClickCreate} />
      </Box>
      <Box marginBottom={2}>
        <Typography variant="h6">All Featured Cards</Typography>
      </Box>
      <Box marginBottom={2} display="flex" justifyContent="space-between">
        <RadioGroup
          row
          value={filterType}
          name="testType"
          onChange={e => {
            setPage(0);
            setFilterType(e.target.value as FeaturedCardType);
          }}
        >
          <FormControlLabel control={<Radio value="all" color="primary" className={''} />} label="Select All" />
          <FormControlLabel control={<Radio value="collection" color="primary" />} label="Collection" />
          <FormControlLabel control={<Radio value="story" color="primary" />} label="Story" />
          <FormControlLabel control={<Radio value="season" color="primary" />} label="Season" />
          <FormControlLabel control={<Radio value="link" color="primary" />} label="Link" />
        </RadioGroup>
        <Box width={320}>
          <SearchTextField
            placeholder="Enter Featured Card Title or Featured ID"
            fullWidth
            isSearching={isSearching}
            onValueSubmit={v => {
              setPage(0);
              setSearchKey(v);
            }}
            onCancel={() => {
              setPage(0);
              setSearchKey('');
            }}
          />
        </Box>
      </Box>
      <FeaturedCardList
        data={featuredCards}
        page={page}
        totalCount={featuredCardsTotalCount}
        onRequestFetch={handleRequestFetch}
        onClickDeleteButton={handleDeleteButtonClick}
        onClickEdit={handleClickEdit}
        getLinkUri={getLinkUri}
      />
      <Modal
        open={showModal}
        onEscapeKeyDown={() => {
          setShowModal(false);
        }}
      >
        <div>
          <AddModal
            onSave={onSaveFeaturedCard}
            onClose={() => {
              setShowModal(false);
            }}
            mode={modalMode}
            featuredCard={editingFeaturedCard}
          />
        </div>
      </Modal>
    </div>
  );
};

export default FeaturedCard;
